import React, { useState, useEffect } from 'react';
import { useNavigation } from '../context/NavigationContext';
import {
  Globe,
  ExternalLink,
  Smartphone,
  Cpu,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Building2,
  Server,
} from 'lucide-react';
import { OfficialPortfolioProject, ProjectType } from '../../types';
import { realtimeDbService } from '../../services/realtimeDbService';
import { INITIAL_OFFICIAL_PORTFOLIO } from '../../data/officialPortfolioData';
import { SafeImage } from './ui/SafeImage';

export const OfficialPortfolioSection: React.FC = () => {
  const { navigate } = useNavigation();
  const [projects, setProjects] = useState<OfficialPortfolioProject[]>(INITIAL_OFFICIAL_PORTFOLIO);
  const [selectedFilter, setSelectedFilter] = useState<'ALL' | ProjectType>('ALL');
  const [showAll, setShowAll] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    const loadProjects = async () => {
      try {
        const data = await realtimeDbService.getOfficialPortfolioProjects();
        if (isMounted && data && data.length > 0) {
          setProjects(data);
        }
      } catch (err) {
        console.warn('Portfolio load notice:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadProjects();
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter only active projects
  const activeProjects = projects.filter((p) => p.status === 'ACTIVE');

  // Filter by category/type
  const filtered = activeProjects.filter((item) => {
    if (selectedFilter === 'ALL') return true;
    return item.projectType === selectedFilter;
  });

  // Decide how many to display
  const displayedProjects = showAll
    ? filtered
    : filtered.filter((p) => p.featured || selectedFilter !== 'ALL');

  const totalWebsites = activeProjects.filter((p) => p.projectType === 'website').length;
  const totalMobile = activeProjects.filter((p) => p.projectType === 'mobile_app').length;
  const totalCustom = activeProjects.filter((p) => p.projectType === 'custom_software').length;

  return (
    <section id="portfolio" className="py-24 sm:py-32 relative bg-slate-950 border-t border-slate-800/80 overflow-hidden">
      {/* Background glow accents */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20 mb-4 shadow-xs">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Gerçekleştirdiğimiz Projeler</span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
            Adiosoft Tarafından <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Geliştirilen Projeler</span>
          </h2>

          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
            Kurumsal web sitelerinden sahaya özel mobil uygulamalara ve kurumsal yazılım altyapılarına kadar farklı sektörlerde hayata geçirdiğimiz gerçek başarı hikayeleri.
          </p>

          {/* Quick Metrics Pills */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs font-semibold text-slate-300">
            <div className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-1.5 shadow-xs">
              <Globe className="w-3.5 h-3.5 text-blue-400" />
              <span>{totalWebsites} Canlı Web Sitesi</span>
            </div>
            <div className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-1.5 shadow-xs">
              <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
              <span>{totalMobile} Özel Mobil Uygulama</span>
            </div>
            <div className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-1.5 shadow-xs">
              <Cpu className="w-3.5 h-3.5 text-indigo-400" />
              <span>{totalCustom} Kurumsal Çözüm</span>
            </div>
          </div>
        </div>

        {/* Filter Navigation Bar */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-2 p-1.5 rounded-2xl bg-slate-900/90 border border-slate-800 max-w-xl mx-auto backdrop-blur-sm">
          <button
            type="button"
            onClick={() => {
              setSelectedFilter('ALL');
              setShowAll(false);
            }}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
              selectedFilter === 'ALL'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Tüm Projeler ({activeProjects.length})
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedFilter('website');
              setShowAll(true);
            }}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 ${
              selectedFilter === 'website'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            <span>Web Siteleri ({totalWebsites})</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedFilter('mobile_app');
              setShowAll(true);
            }}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 ${
              selectedFilter === 'mobile_app'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>Mobil Uygulama ({totalMobile})</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedFilter('custom_software');
              setShowAll(true);
            }}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 ${
              selectedFilter === 'custom_software'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Building2 className="w-3.5 h-3.5" />
            <span>Kurumsal ({totalCustom})</span>
          </button>
        </div>

        {/* Projects Grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {displayedProjects.map((proj) => {
            const isWebsite = proj.projectType === 'website';
            const isMobile = proj.projectType === 'mobile_app';
            const isCustom = proj.projectType === 'custom_software';

            return (
              <div
                key={proj.id}
                className="group rounded-3xl bg-slate-900/70 border border-slate-800/90 overflow-hidden flex flex-col justify-between hover:border-slate-700 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-900/10"
              >
                <div>
                  {/* Card Media Preview */}
                  <div className="relative aspect-[16/10] bg-slate-950 overflow-hidden border-b border-slate-800/80">
                    {proj.thumbnail ? (
                      <SafeImage
                        src={proj.thumbnail}
                        alt={proj.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-950">
                        {isMobile ? (
                          <Smartphone className="w-12 h-12 text-blue-500/40" />
                        ) : isCustom ? (
                          <ShieldCheck className="w-12 h-12 text-indigo-500/40" />
                        ) : (
                          <Globe className="w-12 h-12 text-cyan-500/40" />
                        )}
                      </div>
                    )}

                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />

                    {/* Top Badges */}
                    <div className="absolute top-3.5 left-3.5 right-3.5 flex items-center justify-between pointer-events-none">
                      <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-950/80 backdrop-blur-md text-slate-200 border border-slate-800">
                        {proj.category}
                      </span>

                      {proj.featured && (
                        <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-600/90 backdrop-blur-md text-white border border-blue-400/40 flex items-center gap-1 shadow-sm">
                          <Sparkles className="w-3 h-3 text-amber-300" />
                          <span>Öne Çıkan</span>
                        </span>
                      )}
                    </div>

                    {/* Project Type Bottom Indicator */}
                    <div className="absolute bottom-3 left-3.5 flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                      {isWebsite && (
                        <span className="flex items-center gap-1 bg-slate-900/90 px-2.5 py-1 rounded-md border border-slate-700/80">
                          <Globe className="w-3.5 h-3.5 text-blue-400" />
                          <span>Web Sitesi</span>
                        </span>
                      )}
                      {isMobile && (
                        <span className="flex items-center gap-1 bg-slate-900/90 px-2.5 py-1 rounded-md border border-slate-700/80">
                          <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Özel Mobil Yazılım</span>
                        </span>
                      )}
                      {isCustom && (
                        <span className="flex items-center gap-1 bg-slate-900/90 px-2.5 py-1 rounded-md border border-slate-700/80">
                          <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />
                          <span>Kurumsal Çözüm</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Content Body */}
                  <div className="p-6 space-y-3">
                    <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                      {proj.title}
                    </h3>

                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed line-clamp-3">
                      {proj.description}
                    </p>

                    {/* Technologies Pills */}
                    {proj.technologies && proj.technologies.length > 0 && (
                      <div className="pt-2 flex flex-wrap gap-1.5">
                        {proj.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-800/80 text-slate-300 border border-slate-700/60"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer Action */}
                <div className="px-6 pb-6 pt-2 border-t border-slate-800/60 flex items-center justify-between gap-3">
                  {proj.url ? (
                    <a
                      href={proj.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600/10 hover:bg-blue-600 text-blue-400 hover:text-white font-bold text-xs border border-blue-500/20 hover:border-transparent transition duration-200 shadow-xs"
                    >
                      <span>Web Sitesini Gör</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <div className="w-full flex items-center justify-between text-xs py-1 text-slate-400">
                      <span className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Başarıyla Teslim Edildi</span>
                      </span>
                      <span className="text-[11px] text-slate-500">Adiosoft Özel Çözüm</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Toggle Show All Button */}
        {selectedFilter === 'ALL' && filtered.length > displayedProjects.length && (
          <div className="mt-12 text-center">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="px-8 py-3.5 rounded-2xl bg-slate-900 hover:bg-slate-850 text-slate-200 hover:text-white font-bold text-xs sm:text-sm border border-slate-700 hover:border-slate-600 transition shadow-lg inline-flex items-center gap-2"
            >
              <span>Tüm Referans Projelerini Gör ({activeProjects.length} Proje)</span>
              <ArrowRight className="w-4 h-4 text-blue-400" />
            </button>
          </div>
        )}

        {/* Section Bottom Conversion Banner */}
        <div className="mt-20 p-8 sm:p-10 rounded-3xl bg-gradient-to-r from-blue-950/40 via-slate-900 to-indigo-950/30 border border-blue-500/20 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h4 className="text-xl sm:text-2xl font-black text-white">
              Siz de Kendi Projenizi Hayata Geçirin
            </h4>
            <p className="text-xs sm:text-sm text-slate-400 max-w-xl">
              İster 10 dakikada hazır şablonlarla web sitenizi kurun, ister firmanıza özel mobil ve yazılım çözümlerimiz için teklif alın.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto justify-center">
            <button
              type="button"
              onClick={() => navigate('/kayit')}
              className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-600/30 transition flex items-center gap-2"
            >
              <span>Ücretsiz Başlayın</span>
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => navigate('/iletisim')}
              className="px-5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs sm:text-sm border border-slate-700 transition"
            >
              Özel Proje Teklifi Al
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
